/* The proposal card: boards S and S3 (`.prop`, `.chg`, `.pair`, `.replaced`, `.propf`). Readable
 * labels the boards set in the tertiary ink are drawn in the secondary one, as the rest of the
 * conversation is (the canon README's "Secondary ink for readable text"). */

.card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-color: var(--border-2);
  border-radius: var(--radius-lg);
}

.head {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-14px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-foot);
  font-weight: var(--fw-extrabold);
}

.link {
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: 0;
  transition-duration: var(--dur-fast);
  transition-property: color;
  transition-timing-function: var(--ease);
}

.link:hover {
  color: var(--text);
}

.link:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-hair);
}

.group {
  display: flex;
  flex-direction: column;
}

.headingRow {
  display: flex;
  gap: var(--space-3);
  align-items: baseline;
  justify-content: space-between;
  padding: var(--space-10px) var(--space-14px) var(--space-1);
}

.heading {
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
}

.change {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-1) var(--space-3);
  padding: var(--space-10px) var(--space-14px) var(--space-3);
  border-top: var(--border-hairline) solid var(--border);
  transition-duration: var(--dur-base);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.change > :not(.toggles) {
  grid-column: 1;
}

.kind {
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.changeTitle {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  overflow-wrap: anywhere;
}

.detail {
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.before {
  color: var(--text-2);
}

.reason {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
  overflow-wrap: anywhere;
}

.warning {
  display: flex;
  gap: var(--space-6px);
  align-items: flex-start;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.warningGlyph {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: var(--space-2px);
}

/* Rejected: faded and struck through, still there to take back. */
.rejected {
  opacity: 0.42;
}

.rejected .changeTitle {
  text-decoration: line-through;
}

/* Overtaken by the trainer's own edit (R18): no toggles, one column. */
.outdated {
  grid-template-columns: minmax(0, 1fr);
  opacity: 0.72;
}

.toggles {
  display: flex;
  grid-row: 1 / span 4;
  grid-column: 2;
  gap: var(--space-6px);
  align-self: start;
}

.toggle {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: var(--space-0);
  color: var(--text-2);
  cursor: pointer;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
  box-shadow: var(--shadow-buddy-control);
  transition-duration: var(--dur-base);
  transition-property: color, background-color, border-color, box-shadow;
  transition-timing-function: var(--ease);
}

.toggle:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.toggleOn {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
  box-shadow: var(--shadow-buddy-chosen);
}

.toggleGlyph {
  width: 14px;
  height: 14px;
}

/* A new day and the exercises on it: one dashed frame, decided together. */
.pair {
  margin: var(--space-1) var(--space-10px) var(--space-2);
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-md);
}

.pairNote {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  margin: var(--space-0);
  padding: var(--space-2) var(--space-14px) var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.pairGlyph {
  flex: none;
  width: 13px;
  height: 13px;
}

.paired {
  border-top-color: var(--border);
}

.rejectRow {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-0) var(--space-14px) var(--space-3);
}

.foot {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
  padding: var(--space-3) var(--space-14px);
  border-top: var(--border-hairline) solid var(--border);
}

/* Under review (board U): „Inna wersja" and „Odrzuć wszystko" on a row, „Zastosuj" across the card
 * beneath them, so neither label is cut at the panel's 448px. */
.footReview {
  flex-direction: column;
  align-items: stretch;
}

.footActs {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.rejectAll {
  margin-inline-start: auto;
}

.applyWide {
  justify-content: center;
  width: 100%;
}

.footApplied {
  align-items: center;
  justify-content: space-between;
  border-top: 0;
}

.done {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin: var(--space-0);
  font-size: var(--fs-foot);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.doneGlyph {
  flex: none;
  width: 15px;
  height: 15px;
  color: var(--good-ink);
}

/* The proposal a later message replaced (R16): one quiet line. */
.replaced {
  display: flex;
  gap: var(--space-2);
  justify-content: space-between;
  margin: var(--space-0);
  padding: var(--space-9px) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.replaced b {
  font-weight: var(--fw-bold);
}

.aside {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-lede);
  color: var(--text-2);
}

/* ── A change to an exercise, drawn as the builder's card (board U) ─────────────────────────── */

/* What the marks mean: the board's `.legend`, under the head. */
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-14px);
  margin: var(--space-0);
  padding: var(--space-10px) var(--space-14px) var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.legend b {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* The card spans the change under its words and its toggles, as the board's `.chx` stacks them. */
.change > .exercise {
  display: flex;
  grid-column: 1 / -1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  margin-top: var(--space-6px);
}

/* A replacement's old exercise: the board's `.gone`, one struck line above the new card. The
 * secondary ink rather than the board's tertiary, as everywhere readable in this card. */
.gone {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  min-width: 0;
  margin: var(--space-0);
  padding: var(--space-9px) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  overflow-wrap: anywhere;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-12px);
}

.goneLabel {
  flex: none;
  font-size: var(--fs-micro);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
}
